import React, { ReactNode } from 'react';
import { Typography, LinkButton } from 'fronton-react';
import { ChevronRightIcon } from '@fronton/icons-react';
import { cx } from 'linaria';
import { HeaderModelData } from 'types/model';
import { getShortenRegionName } from 'utils';
import { useTranslation } from 'react-i18next';
import { useHeaderAnalyticsEvent, useRegions, useSideMenuAnalyticsEvent } from 'components/header/hooks';
import { FavoriteAddressViewState } from 'types/favorite-address';
import { getCityPickerDisplay } from './get-city-picker-display';
import { cityPickerLabel, hover, CityPickerRoot, hoveredText, favoriteAddressLayout } from './styles';
import { FavoriteAddressHint } from './favorite-address-hint';
import { isFavoriteAddressEnabled } from '../../../../selectors';
import { useStoreon } from 'storeon/react';
import { getHeaderAnalyticsElementTextByDeliveryType } from './utils';

export const DATA_QA = 'CITY_PICKER';

interface CityPickerProps {
  model: HeaderModelData;
  isVisible: boolean;
  onRequestRegionChange: () => void;
  favoriteAddressView: FavoriteAddressViewState;
  hintShow: boolean;
  onHintClose: () => void;
}

export const CityPicker: React.FC<CityPickerProps> = ({
  model,
  isVisible,
  onRequestRegionChange,
  favoriteAddressView,
  hintShow,
  onHintClose,
}) => {
  const { t } = useTranslation();

  const { defaultRegion, currentRegion } = useRegions(model);
  const onRegionsClickEvent = useSideMenuAnalyticsEvent('region');
  const { cookies } = useStoreon('cookies');
  const isFavoriteAddress = isFavoriteAddressEnabled(cookies);

  const formattedRegionName = getShortenRegionName(currentRegion?.regionName || defaultRegion.regionName);
  const { label, regionName } = getCityPickerDisplay({
    ...favoriteAddressView,
    regionName: formattedRegionName,
    t,
  });

  const headerElementText = getHeaderAnalyticsElementTextByDeliveryType(
    favoriteAddressView.favoriteAddress.deliveryType,
  );
  const onAddressClickEvent = useHeaderAnalyticsEvent(headerElementText);

  const handleButtonClick = () => {
    onRequestRegionChange();

    if (isFavoriteAddress) {
      onAddressClickEvent();
    } else {
      onRegionsClickEvent();
    }
  };

  const renderAddress = (children: ReactNode) =>
    isFavoriteAddress ? (
      <FavoriteAddressHint hintShow={hintShow} onClose={onHintClose}>
        {children}
      </FavoriteAddressHint>
    ) : (
      <div>{children}</div>
    );

  if (!isVisible) return null;

  return (
    <CityPickerRoot data-qa={DATA_QA} className={cx(isFavoriteAddress && favoriteAddressLayout)}>
      {renderAddress(
        <LinkButton
          size="s"
          withExtraClickZone
          onClick={handleButtonClick}
          iconRight={<ChevronRightIcon />}
          variant="accent"
          data-qa="region-modal-button"
          className={hover}
        >
          <div>
            <Typography variant="caption" color="text-secondary" className={cityPickerLabel}>
              {label}
            </Typography>
            <span className={hoveredText}>{regionName}</span>
          </div>
        </LinkButton>,
      )}
    </CityPickerRoot>
  );
};
